/**
 * ZalihaTab — Stock management panel within the Retail page.
 * Hosts a full product table (list/add/edit) + the low-stock panel.
 * Stock adjustment is a SEPARATE next task — not built here.
 */
import { getLowStock, listProducts } from '@gym/api-client';
import type { TaxRateCode } from '@gym/shared';
import { useQuery } from '@tanstack/react-query';
import { Package, PackageX, Plus } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { EmptyState, Skeleton } from '../../components/ui/Feedback.js';
import { Select } from '../../components/ui/Field.js';
import { SearchInput } from '../../components/ui/Layout.js';
import { Money } from '../../components/ui/Money.js';
import { Chip, StatusPill } from '../../components/ui/StatusPill.js';
import { Table, TableSkeleton, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

import { ProductDetailDrawer } from './ProductDetailDrawer.js';
import { ProductFormModal } from './ProductFormModal.js';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProductRow {
  id: string;
  name: string;
  price: number;
  cost: number | null;
  category: string | null;
  sku: string | null;
  barcode: string | null;
  tax_rate_code: TaxRateCode;
  is_active: boolean;
  track_inventory: boolean;
}

interface InventoryQtyRow {
  product_id: string;
  quantity_on_hand: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ZalihaTab(): ReactNode {
  const { t } = useTranslation();
  const { claims, hasRole } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const canManageProducts = hasRole('owner', 'manager');

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<ProductRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Products query ─────────────────────────────────────────────────────────
  const productsQ = useQuery({
    queryKey: ['products', tenantId, 'all'],
    enabled: !!tenantId,
    queryFn: () => listProducts(supabase),
  });

  // ── Inventory quantities ───────────────────────────────────────────────────
  // Returns the raw rows (NOT a Map) so this query shares the exact same cache
  // shape as KasaTab's ['inventory_qty', tenantId] query — each component builds
  // its own Map from the rows. (Returning a Map here caused a cross-tab cache
  // collision: KasaTab cached an array, then qtyMap.get() blew up.)
  const inventoryQtyQ = useQuery({
    queryKey: ['inventory_qty', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<InventoryQtyRow[]> => {
      const { data, error } = await supabase
        .from('inventory_items')
        .select('product_id, quantity_on_hand');
      if (error) throw error;
      return (data ?? []) as InventoryQtyRow[];
    },
  });

  // ── Low-stock panel ────────────────────────────────────────────────────────
  const lowStockQ = useQuery({
    queryKey: ['low_stock', tenantId],
    enabled: !!tenantId,
    queryFn: () => getLowStock(supabase),
  });

  // ── Distinct non-null categories present in the product list ────────────────
  const categories = useMemo((): string[] => {
    const raw = (productsQ.data ?? []) as unknown as ProductRow[];
    const set = new Set<string>();
    for (const p of raw) if (p.category) set.add(p.category);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [productsQ.data]);

  // ── Client-side filters: search + category + status ────────────────────────
  const products = useMemo((): ProductRow[] => {
    const raw = (productsQ.data ?? []) as unknown as ProductRow[];
    const term = search.trim().toLowerCase();
    return raw.filter((p) => {
      if (term && !p.name.toLowerCase().includes(term)) return false;
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      if (statusFilter === 'active' && !p.is_active) return false;
      if (statusFilter === 'inactive' && p.is_active) return false;
      return true;
    });
  }, [productsQ.data, search, categoryFilter, statusFilter]);

  const qtyMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of inventoryQtyQ.data ?? []) {
      m.set(row.product_id, (m.get(row.product_id) ?? 0) + Number(row.quantity_on_hand));
    }
    return m;
  }, [inventoryQtyQ.data]);

  // ── Row click ──────────────────────────────────────────────────────────────
  function openDetail(p: ProductRow): void {
    setSelected(p);
    setDrawerOpen(true);
  }

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* ── Main product table (2/3 width) ─────────────────────────────────── */}
      <div className="lg:col-span-2">
        {/* Toolbar: search + category + status filters + Add */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('retail.searchProducts')}
            aria-label={t('retail.searchProducts')}
            className="min-w-[12rem] flex-1"
          />
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label={t('retail.filter.category')}
            className="w-auto"
          >
            <option value="all">{t('common.all')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            aria-label={t('retail.filter.status')}
            className="w-auto"
          >
            <option value="all">{t('common.all')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </Select>
          {canManageProducts ? (
            <Button variant="primary" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('retail.product.add')}
            </Button>
          ) : null}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>{t('retail.product.name')}</TH>
                  <TH numeric>{t('retail.product.price')}</TH>
                  <TH numeric>{t('retail.stock')}</TH>
                  <TH>{t('retail.product.category')}</TH>
                  <TH>{t('retail.product.active')}</TH>
                </tr>
              </THead>

              {productsQ.isLoading ? (
                <TableSkeleton rows={5} cols={5} />
              ) : products.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={5} className="py-10">
                      <EmptyState
                        icon={Package}
                        title={t('retail.noProducts')}
                        hint={t('retail.noProductsHint')}
                      />
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {products.map((p) => {
                    // Stock is always an integer — products with no inventory row read 0.
                    const qty = qtyMap.get(p.id) ?? 0;
                    return (
                      <TR key={p.id} clickable onClick={() => openDetail(p)}>
                        <TD>
                          <span className="font-medium">{p.name}</span>
                          {p.sku ? (
                            <span className="ml-2 font-mono text-xs text-muted">{p.sku}</span>
                          ) : null}
                        </TD>
                        <TD numeric>
                          <Money minor={p.price} />
                        </TD>
                        <TD numeric>
                          <span className="tnum">{qty}</span>
                        </TD>
                        <TD>
                          {p.category ? (
                            <Chip tone="neutral">{p.category}</Chip>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </TD>
                        <TD>
                          <StatusPill
                            status={p.is_active ? 'active' : 'inactive'}
                            label={p.is_active ? t('common.active') : t('common.inactive')}
                          />
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              )}
            </Table>
          </TableWrap>
        </div>
      </div>

      {/* ── Low-stock panel (1/3 width) ────────────────────────────────────── */}
      <div>
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          <div className="flex items-center gap-2 border-b border-border px-5 py-3">
            <PackageX className="h-4 w-4 text-warning" aria-hidden />
            <h3 className="font-display text-base font-semibold text-fg">{t('retail.lowStock')}</h3>
          </div>
          <div className="divide-y divide-border">
            {lowStockQ.isLoading ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : (lowStockQ.data ?? []).length === 0 ? (
              <p className="p-4 text-sm text-muted">{t('retail.noLowStock')}</p>
            ) : (
              (lowStockQ.data ?? []).map((l) => (
                <div
                  key={l.inventory_item_id}
                  className="flex items-center justify-between gap-3 px-5 py-2.5"
                >
                  <span className="text-sm text-fg">{l.product_name}</span>
                  <Chip tone={l.quantity_on_hand === 0 ? 'danger' : 'warning'}>
                    {l.quantity_on_hand} / {l.threshold}
                  </Chip>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Modals / drawers ────────────────────────────────────────────────── */}
      <ProductFormModal
        open={addOpen}
        onOpenChange={setAddOpen}
        product={null}
        tenantId={tenantId}
      />

      <ProductDetailDrawer
        open={drawerOpen}
        onOpenChange={(o) => {
          setDrawerOpen(o);
          if (!o) setSelected(null);
        }}
        product={selected}
        tenantId={tenantId}
      />
    </div>
  );
}
